import { useQuery } from "@tanstack/react-query";
import { fetchJson } from "common/utils/fetchJson";

type FastestDeliveryStockResponse = {
  enabled: boolean;
  optimalStockLocationIds: number[];
  availableCompositionIds: number[];
  variantStockCounts: Record<string, number>;
  variantTotalStockCounts: Record<string, number>;
};

/**
 * Fetches fastest delivery stock data for a product based on optimal DC locations.
 * The API uses the user's session zipcode to determine nearby stock locations.
 */
export function useFastestDeliveryStock(catalogNumber?: string) {
  return useQuery({
    enabled: !!catalogNumber,
    queryKey: catalogNumber ? getFastestDeliveryStockQueryKey(catalogNumber) : [],
    queryFn: async () => {
      if (!catalogNumber) return undefined;
      return fetchFastestDeliveryStock(catalogNumber);
    },
    staleTime: 5 * 60 * 1000 // 5 minutes
  });
}

const getFastestDeliveryStockQueryKey = (catalogNumber: string) => [
  "fastestDeliveryStock",
  catalogNumber
];

const fetchFastestDeliveryStock = async (
  catalogNumber: string
): Promise<FastestDeliveryStockResponse | undefined> => {
  type RawResponse = {
    enabled: boolean;
    optimal_stock_location_ids: number[];
    available_composition_ids: number[];
    variant_stock_counts: Record<string, number>;
    variant_total_stock_counts: Record<string, number>;
  };

  const data = await fetchJson<RawResponse>(
    `/api/products/${catalogNumber}/fastest_delivery_stock`
  );

  if (!data) return undefined;

  // Convert snake_case keys to camelCase
  return {
    enabled: data.enabled,
    optimalStockLocationIds: data.optimal_stock_location_ids ?? [],
    availableCompositionIds: data.available_composition_ids ?? [],
    variantStockCounts: data.variant_stock_counts ?? {},
    variantTotalStockCounts: data.variant_total_stock_counts ?? {}
  };
};
